import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Pencil, Loader2, Youtube } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageUpload from "@/components/tournament/ImageUpload";

function EditMedia({ g, onSaved }) {
  const [open, setOpen] = useState(false);
  const [shot, setShot] = useState(g.screenshot || "");
  const [url, setUrl] = useState(g.trailer_id ? `https://youtu.be/${g.trailer_id}` : "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put(`/gamehub/item/${g.id}/media`, { screenshot: shot, trailer_url: url }); onSaved(r.data); setOpen(false); toast.success("Media updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="edit-media-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-xs font-bold text-white/70 hover:text-white"><Pencil size={12} /> Edit screenshot & trailer</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d1117] border-white/10 text-white max-w-md" data-testid="edit-media-modal">
          <DialogHeader><DialogTitle className="font-head">Screenshot & trailer</DialogTitle></DialogHeader>
          <ImageUpload value={shot} onChange={setShot} testid="edit-media-screenshot" aspect="aspect-video" label="Upload screenshot" endpoint="/gamehub/upload" maxMB={8} />
          <div className="relative"><Youtube size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500" />
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtu.be/…" data-testid="edit-media-trailer"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-black/30 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]" /></div>
          <button onClick={save} disabled={busy} data-testid="edit-media-save" className="w-full py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-bold disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : "Save"}</button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Trailer({ g }) {
  const ref = useRef(null);
  const [play, setPlay] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || play) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setPlay(true); io.disconnect(); } }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [play]);
  return (
    <div ref={ref} className="relative aspect-video rounded-xl overflow-hidden bg-black" data-testid="game-trailer" data-playing={play ? "true" : "false"}>
      {play && <iframe src={`https://www.youtube-nocookie.com/embed/${g.trailer_id}?rel=0&autoplay=1&mute=1&playsinline=1`} title={`${g.name} trailer`} className="absolute inset-0 w-full h-full"
        referrerPolicy="strict-origin-when-cross-origin" data-testid="game-trailer-iframe"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />}
    </div>
  );
}

export default function GameMedia({ g, canEdit, onSaved }) {
  const [zoom, setZoom] = useState(false);
  const has = g.screenshot || g.trailer_id;
  if (!has && !canEdit) return null;
  return (
    <section className="mt-10" data-testid="game-media">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold text-white">Trailer & Screenshot</h2>
        {canEdit && <EditMedia g={g} onSaved={onSaved} />}
      </div>
      {!has ? <p className="text-sm text-white/40 card-surface rounded-xl p-5">Add a screenshot and a YouTube trailer to show off your game.</p> : (
        <div className={`grid gap-3 ${g.screenshot && g.trailer_id ? "md:grid-cols-2" : ""}`}>
          {g.trailer_id && <Trailer key={g.trailer_id} g={g} />}
          {g.screenshot && (
            <button onClick={() => setZoom(true)} data-testid="game-screenshot" className="group relative aspect-video rounded-xl overflow-hidden bg-black">
              <img src={mediaUrl(g.screenshot)} alt={`${g.name} screenshot`} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-black/70 text-[11px] text-white/80"><ImageIcon size={12} /> Click to enlarge</span>
            </button>
          )}
        </div>
      )}
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="bg-black border-white/10 p-2 max-w-6xl w-[96vw]" data-testid="screenshot-lightbox">
          <img src={mediaUrl(g.screenshot)} alt={`${g.name} screenshot`} className="w-full max-h-[85vh] object-contain rounded" />
        </DialogContent>
      </Dialog>
    </section>
  );
}
